import { useState, useEffect, useRef } from "react";
import { App, AppState } from "@capacitor/app";

/**
 * A custom hook that simulates a typewriter effect for a given string.
 * This version is specifically designed to handle a target `text` that grows over time
 * (e.g., from a stream), ensuring the animation continues smoothly without restarting.
 * It also handles the app resuming from the background, preventing the animation from being stuck.
 * @param text The full text to type out.
 * @param speed The typing speed in milliseconds per character.
 * @param initialValue The initial value of the displayed text.
 * @returns The portion of the text that should be displayed at the current time.
 */
export const useTypewriter = (text: string, speed: number = 60, initialValue: string = "") => {
  const [displayedText, setDisplayedText] = useState(initialValue);
  // Use a ref to hold the target text. This allows the interval and other listeners
  // to access the latest text value without needing to be recreated.
  const currentTextRef = useRef(text);
  currentTextRef.current = text;

  // This effect handles the "reset" condition.
  // If the target text changes to something that doesnt start with whats currently displayed,
  // it resets the animation. This is for cases like switching to a new team.
  useEffect(() => {
    if (displayedText.length > 0 && !text.startsWith(displayedText)) {
      setDisplayedText("");
    }
  }, [text]);

  // This effect is the typewriter "engine". It also includes logic
  // to handle the app resuming from the background.
  useEffect(() => {
    // This function "fast-forwards" the displayed text to the current
    // complete text. This is crucial for when the app returns to the
    // foreground, as the data stream may have continued while the UI was frozen.
    const handleResume = () => {
      setDisplayedText(currentTextRef.current);
    };

    // Set up listeners for when the app/tab becomes visible again.
    // Capacitor listener for native mobile apps.
    const appStateListener = App.addListener("appStateChange", (state: AppState) => {
      if (state.isActive) {
        handleResume();
      }
    });

    // Standard web browser listener for desktop.
    const handleVisibilityChange = () => {
      if (document.visibilityState === "visible") {
        handleResume();
      }
    };
    document.addEventListener("visibilitychange", handleVisibilityChange);

    const intervalId = setInterval(() => {
      // Use the functional form of setDisplayedText to avoid stale state.
      setDisplayedText((prev) => {
        // If we have finished typing the current target text, do nothing and wait.
        // The interval will keep running, checking for new text on the next tick.
        if (prev.length >= currentTextRef.current.length) {
          return prev;
        }
        // Otherwise, add the next character.
        // return currentTextRef.current.substring(0, prev.length + 1);

        // When speed is 0, add a larger chunk of characters per interval to make the effect
        // appear faster than what a simple setInterval(..., 0) can achieve due to browser limitations.
        const charsPerInterval = speed === 0 ? 15 : 10;
        const nextLength = Math.min(prev.length + charsPerInterval, currentTextRef.current.length);

        // Otherwise, add the next character(s).
        return currentTextRef.current.substring(0, nextLength);
      });
    }, speed);

    // The cleanup function runs ONLY when the component unmounts or speed changes.
    return () => {
      clearInterval(intervalId);
      // It's important to clean up listeners to prevent memory leaks.
      appStateListener.then((listener) => listener.remove());
      document.removeEventListener("visibilitychange", handleVisibilityChange);
    };
  }, [speed]); // The engine only restarts if the speed changes.

  return displayedText;
};
